'use client';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { Variant } from '../shared.types';

export interface Message {
  variant?: Variant;
  id: string;
  title: string;
  message: string;
  timestamp: number;
}

export interface ToasterState {
  messages: Message[];
  actions: {
    sendMessage: (title: string, message: string, variant?: Variant) => void;
    deleteMessage: (id: string) => void;
  };
}

export const useToasterStore = create<ToasterState>()(
  devtools((set) => ({
    messages: [],
    actions: {
      sendMessage: (title: string, message: string, variant?: Variant) =>
        set((state: ToasterState) => {
          const timestamp = Date.now();

          // A repeat of something already on screen refreshes it rather than
          // stacking beside it. Scoped to what is visible rather than to a window
          // of time because the thing this exists to absorb — one request retried
          // — spaces its attempts by an exponential backoff, so any window short
          // enough to be meaningful is one the retries step straight over. The
          // whole list is searched, not just the tail: two failures interleaving
          // put something else in between.
          const duplicate = state.messages.find(
            (item) =>
              item.title === title &&
              item.message === message &&
              item.variant === variant,
          );

          if (duplicate) {
            // Restarting the clock keeps the toast up for its full life after the
            // last attempt, so the message does not vanish mid-retry.
            return {
              messages: state.messages.map((item) =>
                item.id === duplicate.id ? { ...item, timestamp } : item,
              ),
            };
          }

          return {
            messages: [
              ...state.messages,
              {
                id: crypto.randomUUID(),
                title,
                message,
                variant,
                timestamp,
              },
            ],
          };
        }),
      deleteMessage: (id: string) =>
        set((state: ToasterState) => ({
          messages: state.messages.filter((message) => message.id !== id),
        })),
    },
  })),
);

export const useToaster = () => useToasterStore((state) => state.messages);

export const useToasterActions = () =>
  useToasterStore((state) => state.actions);
